import { isBrazilianUf, normalizeUf } from "@/lib/brazilianUf";
import { formatCep, isValidCep, maskCepInput, normalizeCep } from "@/lib/cep";
import { formatCnpj, isValidCnpj, normalizeCnpj } from "@/lib/cnpj";
import type { CountryKey } from "@/lib/countryProfile";
import { formatEin, isValidEin, normalizeEin } from "@/lib/ein";
import { isValidPhone, normalizePhone } from "@/lib/phone";
import { isUsState, normalizeUsState } from "@/lib/usStates";
import { formatCpf, isValidCpf, normalizeCpf } from "@/lib/cpf";
import { formatSsn, isValidSsn, normalizeSsn } from "@/lib/ssn";

export function normalizePostalCode(value: string): string {
  return value.replace(/\D/g, "");
}

export function isValidZip(value: string): boolean {
  const digits = normalizePostalCode(value);
  return digits.length === 5 || digits.length === 9;
}

export function formatZip(value: string): string {
  const digits = normalizePostalCode(value).slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

export function maskZipInput(raw: string): string {
  return formatZip(raw);
}

export function formatPostalCode(value: string, countryKey: CountryKey): string {
  switch (countryKey) {
    case "BR":
      return formatCep(value);
    case "US":
      return formatZip(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function maskPostalCodeInput(raw: string, countryKey: CountryKey): string {
  switch (countryKey) {
    case "BR":
      return maskCepInput(raw);
    case "US":
      return maskZipInput(raw);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function isValidPostalCode(value: string, countryKey: CountryKey): boolean {
  switch (countryKey) {
    case "BR":
      return isValidCep(value);
    case "US":
      return isValidZip(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function normalizePostalCodeForCountry(value: string, countryKey: CountryKey): string {
  switch (countryKey) {
    case "BR":
      return normalizeCep(value);
    case "US":
      return normalizePostalCode(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function isValidRegion(value: string, countryKey: CountryKey): boolean {
  const normalized = value.trim().toUpperCase();
  if (normalized.length !== 2) return false;
  switch (countryKey) {
    case "BR":
      return isBrazilianUf(normalized);
    case "US":
      return isUsState(normalized);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function normalizeRegion(value: string, countryKey: CountryKey): string {
  switch (countryKey) {
    case "BR":
      return normalizeUf(value);
    case "US":
      return normalizeUsState(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function isValidTaxId(value: string, countryKey: CountryKey): boolean {
  switch (countryKey) {
    case "BR":
      return isValidCnpj(value);
    case "US":
      return isValidEin(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function normalizeTaxId(value: string, countryKey: CountryKey): string {
  switch (countryKey) {
    case "BR":
      return normalizeCnpj(value);
    case "US":
      return normalizeEin(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function formatTaxId(value: string, countryKey: CountryKey): string {
  switch (countryKey) {
    case "BR":
      return formatCnpj(value);
    case "US":
      return formatEin(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function maskTaxIdInput(raw: string, countryKey: CountryKey): string {
  return formatTaxId(raw, countryKey);
}

export function isValidPersonTaxId(value: string, countryKey: CountryKey): boolean {
  switch (countryKey) {
    case "BR":
      return isValidCpf(value);
    case "US":
      return isValidSsn(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function normalizePersonTaxId(value: string, countryKey: CountryKey): string {
  switch (countryKey) {
    case "BR":
      return normalizeCpf(value);
    case "US":
      return normalizeSsn(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function formatPersonTaxId(value: string, countryKey: CountryKey): string {
  switch (countryKey) {
    case "BR":
      return formatCpf(value);
    case "US":
      return formatSsn(value);
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function maskPersonTaxIdInput(raw: string, countryKey: CountryKey): string {
  return formatPersonTaxId(raw, countryKey);
}

export function isValidPhoneForCountry(value: string, countryKey: CountryKey): boolean {
  const digits = normalizePhone(value);
  switch (countryKey) {
    case "BR":
      return isValidPhone(value);
    case "US":
      return digits.length === 10;
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}

export function formatPhoneForCountry(value: string, countryKey: CountryKey): string {
  const digits = normalizePhone(value);
  switch (countryKey) {
    case "BR": {
      if (digits.length === 0) return "";
      if (digits.length <= 2) return `(${digits}`;
      const ddd = digits.slice(0, 2);
      const rest = digits.slice(2);
      if (digits.length <= 6) return `(${ddd}) ${rest}`;
      if (digits.length <= 10) return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
      return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5, 9)}`;
    }
    case "US": {
      const clipped = digits.slice(0, 10);
      if (clipped.length === 0) return "";
      if (clipped.length <= 3) return `(${clipped}`;
      if (clipped.length <= 6) return `(${clipped.slice(0, 3)}) ${clipped.slice(3)}`;
      return `(${clipped.slice(0, 3)}) ${clipped.slice(3, 6)}-${clipped.slice(6)}`;
    }
    default: {
      const _exhaustive: never = countryKey;
      return _exhaustive;
    }
  }
}
